/**
 * DraftTextField Component
 *
 * A text field that integrates with the existing draft state pattern used in the app.
 */

import React, { useState } from 'react';
import { Grid, InputAdornment, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { TextField } from './TextField';
import { colors, typography as typographyTokens, radii } from '../../tokens';

export interface DraftTextFieldProps<State extends Record<string, unknown>> {
    /** Field label */
    label: string;
    /** State key to update */
    stateKey: string & keyof State;
    /** Draft state tuple [state, setState] */
    draft: [State, React.Dispatch<React.SetStateAction<State>>];
    /** Validation function */
    validateFn?: (value: string) => boolean;
    /** Helper text for valid/invalid states */
    helperText?: {
        valid: string;
        invalid: string;
    };
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Whether the field is read-only */
    readOnly?: boolean;
    /** Input type */
    type?: 'text' | 'email' | 'password' | 'number' | 'tel';
    /** Placeholder text */
    placeholder?: string;
    /** Grid size (out of 12) */
    size?: number;
    /** Whether to show required indicator */
    required?: boolean;
    /** Whether the field is multiline */
    multiline?: boolean;
    /** Number of rows for multiline */
    rows?: number;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const DraftTextField = <State extends Record<string, unknown>>({
    label,
    stateKey,
    draft,
    validateFn,
    helperText = { valid: '', invalid: '' },
    disabled = false,
    readOnly = false,
    type = 'text',
    placeholder,
    size = 12,
    required = false,
    sx,
    multiline = false,
    rows,
}: DraftTextFieldProps<State>) => {
    const [draftState, setDraftState] = draft;
    const [isValid, setIsValid] = useState(true);
    const [touched, setTouched] = useState(false);

    const currentValue = String(draftState?.[stateKey] ?? '');

    const handleChange = (value: string) => {
        setDraftState((prev) => ({
            ...prev,
            [stateKey]: value,
        }));

        if (validateFn && touched) {
            setIsValid(validateFn(value));
        }
    };

    const handleBlur = () => {
        setTouched(true);
        if (validateFn) {
            setIsValid(validateFn(currentValue));
        }
    };

    const showError = touched && !isValid;

    return (
        <Grid item xs={12} sm={12} md={size} sx={sx}>
            <TextField
                label={label}
                value={currentValue}
                onChange={handleChange}
                type={type}
                placeholder={placeholder}
                disabled={disabled}
                readOnly={readOnly}
                required={required}
                error={showError}
                errorMessage={showError ? helperText.invalid : undefined}
                helperText={!showError ? helperText.valid : undefined}
                multiline={multiline}
                rows={rows}
                endAdornment={readOnly ? <LockIcon sx={{ fontSize: 18, color: colors.neutral[400] }} /> : undefined}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '&:focus-within': {
                            backgroundColor: alpha(colors.primary[500], 0.02),
                        },
                    },
                }}
            />
        </Grid>
    );
};

export default DraftTextField;
